import { createAsyncThunk } from "@reduxjs/toolkit";
import { APP_PATH } from "../../api/backendPath";
import axios from "axios";
import { handleAccountDeactivated } from "../../utils/accountDeactivated";

const createAxiosInstance = () => {
  const token = sessionStorage.getItem('usr_1a2b3c');
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Auto-show the block modal when the admin deactivates this account
  // (notifications poll frequently, so this reliably triggers it).
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      handleAccountDeactivated(error);
      return Promise.reject(error);
    },
  );
  return instance;
};

// Get user notifications
export const getUserNotifications = createAsyncThunk(
  "notifications/getUserNotifications",
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${APP_PATH.getUserNotifications}?page=${page}&limit=${limit}`
      );
      return response.data?.data || { notifications: [], pagination: { total: 0, page: 1, limit: 20, totalPages: 0 } };
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch notifications");
    }
  }
);

// Get unread notification count
export const getUnreadNotificationCount = createAsyncThunk(
  "notifications/getUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(APP_PATH.getUnreadNotificationCount);
      return response.data?.data?.count || response.data?.data?.unread_count || 0;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch unread count");
    }
  }
);

// Mark notification as read
export const markNotificationRead = createAsyncThunk(
  "notifications/markRead",
  async ({ notificationId }, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.put(`${APP_PATH.markNotificationRead}/${notificationId}/read`);
      return notificationId;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to mark as read");
    }
  }
);

// Mark all notifications as read
export const markAllNotificationsRead = createAsyncThunk(
  "notifications/markAllRead",
  async (_, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.put(APP_PATH.markAllNotificationsRead);
      return true;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to mark all as read");
    }
  }
);

// Delete notification
export const deleteNotification = createAsyncThunk(
  "notifications/delete",
  async ({ notificationId }, { rejectWithValue }) => {
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.delete(`${APP_PATH.deleteNotification}/${notificationId}`);
      return notificationId;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to delete notification");
    }
  }
);
